import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, Edit } from 'lucide-react';
import { getItemsServices, getWorkersDropdown, createJobItem } from '../../services/api';
import { typeLabels } from './JobHelpers';

export function ActualItemRow({ item, onUpdate, onDelete, onChange, hideVariance = false }) {
  const [qty, setQty] = useState(item.actual_qty || 0);
  const [cost, setCost] = useState(item.actual_cost || 0);
  const total = qty * cost;
  const variance = (item.quoted_total || 0) - total;

  useEffect(() => {
    if (onChange) onChange(item, qty, cost, total);
  }, [qty, cost]);

  return (
    <tr className="border-b hover:bg-slate-50">
      <td className="p-1 text-center" style={{width:'24px'}}>
        {item.is_additional && item.job_item_id && (
          <Trash2 size={12} className="text-red-500 cursor-pointer hover:text-red-700" onClick={() => onDelete(item.job_item_id)} data-testid={`delete-item-${item.job_item_id}`} />
        )}
      </td>
      <td className="p-1 text-xs">{item.item_name}</td>
      <td className="p-1 text-center text-xs" style={{width:'48px'}}>{item.actual_uom || item.quoted_uom}</td>
      <td className="p-1" style={{width:'64px'}}>
        <input type="number" value={qty} onChange={e => setQty(parseFloat(e.target.value) || 0)}
          className="w-full px-1 py-0.5 border rounded text-xs text-center bg-slate-50" data-testid={`actual-qty-${item.job_item_id || 'new'}`} step="0.5" min="0" />
      </td>
      <td className="p-1" style={{width:'80px'}}>
        <input type="number" step="0.01" value={cost} onChange={e => setCost(parseFloat(e.target.value) || 0)}
          className="w-full px-1 py-0.5 border rounded text-xs text-right bg-slate-50" data-testid={`actual-cost-${item.job_item_id || 'new'}`} min="0" />
      </td>
      <td className="p-1" style={{width:'80px'}}><input type="text" value={total.toFixed(2)} readOnly className="w-full px-1 py-0.5 border rounded text-xs text-right bg-gray-100" /></td>
      {!hideVariance && (
        <td className={`p-1 text-right text-xs font-medium ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-slate-500'}`} style={{width:'72px'}}>
          {variance.toFixed(2)}
        </td>
      )}
    </tr>
  );
}

// === ADD ITEM FORM (per category) ===


export function AddItemForm({ itemType, jobDescriptionId, onAdded, existingItems, showSellPrice = false }) {
  const [showForm, setShowForm] = useState(false);
  const [itemName, setItemName] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [uom, setUom] = useState('');
  const [qty, setQty] = useState('');
  const [cost, setCost] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const total = (parseFloat(qty) || 0) * (parseFloat(cost) || 0);

  const { data: itemsServices } = useQuery({
    queryKey: ['itemsServices', itemType],
    queryFn: () => getItemsServices({ item_type: itemType }).then(r => r.data),
    enabled: showForm
  });

  const addMutation = useMutation({
    mutationFn: createJobItem,
    onSuccess: () => {
      toast.success('Item added');
      setItemName(''); setSelectedItemId(''); setUom(''); setQty(''); setCost(''); setSellPrice('');
      setShowForm(false);
      onAdded();
    },
    onError: () => toast.error('Failed to add item')
  });

  const handleSelectItem = (e) => {
    const val = e.target.value;
    setSelectedItemId(val);
    if (val) {
      const found = itemsServices?.find(i => i.id === val);
      if (found) {
        setItemName(found.name);
        setUom(found.uom || '');
        setCost(found.cost || '');
        if (showSellPrice) setSellPrice(found.sell_price || '');
      }
    }
  };

  const handleAdd = () => {
    if (!itemName) { toast.error('Please enter item name'); return; }
    const q = parseFloat(qty) || 0;
    const c = parseFloat(cost) || 0;
    addMutation.mutate({
      job_description_id: jobDescriptionId,
      item_type: itemType,
      item_name: itemName,
      uom: uom,
      qty: q,
      cost: c,
      sell_price: parseFloat(sellPrice) || 0,
      // This form lives on the Variance "Actual" side — the entered figures ARE
      // the actuals, so persist them immediately (no separate "Save Actuals" step).
      actual_qty: q,
      actual_cost: c,
    });
  };

  return (
    <div className="mb-2">
      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="px-3 py-1 text-xs bg-cyan-500 text-white rounded hover:bg-cyan-600 flex items-center gap-1 float-right" data-testid={`add-item-btn-${itemType}`}>
          <Plus size={12} /> Add Item
        </button>
      ) : (
        <div className="bg-slate-50 border rounded p-3 mb-2" data-testid={`add-item-form-${itemType}`}>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Item</label>
              {itemsServices && itemsServices.length > 0 ? (
                <>
                  <select value={selectedItemId} onChange={handleSelectItem} className="w-full px-2 py-1.5 border rounded text-xs">
                    <option value="">Select Item</option>
                    {itemsServices.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                  <input type="text" value={itemName} onChange={e => setItemName(e.target.value)} placeholder="Or type item name" className="w-full px-2 py-1.5 border rounded text-xs mt-1" data-testid={`add-item-name-${itemType}`} />
                </>
              ) : (
                <input type="text" value={itemName} onChange={e => setItemName(e.target.value)} placeholder="Item name" className="w-full px-2 py-1.5 border rounded text-xs" data-testid={`add-item-name-${itemType}`} />
              )}
            </div>
            <div className={`grid ${showSellPrice ? 'grid-cols-6' : 'grid-cols-5'} gap-2`}>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">UOM</label>
                <input type="text" value={uom} onChange={e => setUom(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs bg-slate-100" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">QTY</label>
                <input type="number" value={qty} onChange={e => setQty(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs" data-testid={`add-item-qty-${itemType}`} />
              </div>
              {showSellPrice && (
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Sell</label>
                  <input type="number" step="0.01" value={sellPrice} onChange={e => setSellPrice(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs" data-testid={`add-item-sell-${itemType}`} />
                </div>
              )}
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Cost</label>
                <input type="number" step="0.01" value={cost} onChange={e => setCost(e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs" data-testid={`add-item-cost-${itemType}`} />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Total</label>
                <input type="text" value={total.toFixed(2)} readOnly className="w-full px-2 py-1.5 border rounded text-xs bg-gray-100" />
              </div>
              <div className="flex items-end">
                <button onClick={handleAdd} disabled={addMutation.isPending} className="w-full px-2 py-1.5 bg-cyan-500 text-white rounded text-xs hover:bg-cyan-600" data-testid={`add-item-submit-${itemType}`}>
                  {addMutation.isPending ? '...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// === VARIANCE MODAL (per description, side-by-side Quoted vs Actual with inline editing) ===
